import type { AiUsagePeriod } from './aiUsageApi';

/**
 * State behind the period controls of the AI budget breakdown: a month picker
 * or a from/to pair. Kept apart from the API period so the form can hold a
 * half-edited (invalid) range without firing a request.
 */
export interface PeriodDraft {
  mode: 'month' | 'range';
  month: string;
  from: string;
  to: string;
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** This month, and a range from the 1st to today — what the page opens on. */
export function initialPeriodDraft(now: Date = new Date()): PeriodDraft {
  const today = dateKey(now);
  return { mode: 'month', month: today.slice(0, 7), from: `${today.slice(0, 7)}-01`, to: today };
}

/** The API period for a draft, or null while the range is incomplete/inverted. */
export function periodFromDraft(draft: PeriodDraft): AiUsagePeriod | null {
  if (draft.mode === 'month') return { kind: 'month', month: draft.month };
  if (!draft.from || !draft.to || draft.from > draft.to) return null;
  return { kind: 'range', from: draft.from, to: draft.to };
}

/** "septembrie 2026" for a "YYYY-MM" key, in the UI locale. */
export function monthName(month: string, locale: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

/** "28 sept. 2026" for a "YYYY-MM-DD" key, in the UI locale. */
export function dayName(day: string, locale: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(locale, { dateStyle: 'medium' });
}
